import { PageLink as Link } from 'src/app/AppAuth/PageLink'
import { GridRow } from 'src/components/Reusable/Grid/Grid'
import { outlineIconColor } from 'src/components/Icons/outlineIconColor'
import palette from 'src/designSystem/variables/palette'
import styled from 'styled-components/macro'

export const ContextTypeSelectionModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const ContextTypeSelectionModalTitle = styled.h3`
  font-weight: 500;
  font-size: 24px;
  line-height: 30px;
  color: ${palette.neutral[900]};
`

export const ContextTypeSelectionBaseText = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
  color: ${palette.neutral[900]};
`

export const ContextTypeSelectionSmallText = styled.p`
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  color: ${palette.neutral[900]};
`

export const ContextTypeSelectorContainer = styled.div<{
  $fullHeight?: boolean
  $column?: boolean
}>`
  align-items: center;
  display: flex;
  flex-direction: ${({ $column }) => ($column ? 'column' : 'row')};
  flex-wrap: wrap;
  justify-content: center;
  gap: ${({ $column }) => ($column ? '12px' : '24px 12px')};
  border: none;
  background-color: transparent;
  ${({ $fullHeight }) => $fullHeight && `height: 100%;`}
  button {
    border: 1px solid ${palette.neutral[200]};
    color: ${palette.neutral[900]};
    padding: 12px;
    font-weight: 400;
  }
`

export const ContextTypeSelectorButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
`

export const ContextTypeSelectorButton = styled.div<{
  $singleSelect?: boolean
  $disabled?: boolean
  $withRadius?: boolean
}>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 220px;
  height: 100px;
  border: 1px solid ${palette.neutral[100]};
  border-radius: ${({ $withRadius }) => ($withRadius ? '14px' : '0px')};
  &:hover {
    background-color: ${palette.neutral[50]};
  }
  &:active {
    background-color: ${palette.neutral[30]};
  }
  ${({ $singleSelect }) =>
    $singleSelect &&
    `
    background-color: ${palette.neutral[500]};
    &:hover {
      background-color: ${palette.neutral[400]};
      p {
        color: ${palette.neutral.white};
      }
    }
    &:active {
      background-color: ${palette.neutral[300]};
      p {
        color: ${palette.neutral.white};
      }
    }
    p {
      color: ${palette.neutral.white};
    }
  `}
  svg {
    width: 24px;
    height: 24px;
    path {
      fill: ${({ $singleSelect }) =>
        $singleSelect ? palette.neutral.white : palette.neutral[500]};
    }
  }
  ${({ $singleSelect }) =>
    outlineIconColor(
      $singleSelect ? palette.neutral.white : palette.neutral[500],
    )}
`

export const ContextTypeSelectorTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

export const ContextTypeSelectorTitle = styled(ContextTypeSelectionSmallText)<{
  $center?: boolean
}>`
  ${({ $center }) =>
    $center &&
    `
    margin: auto;
  `}
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

export const ContextTypeSelectorSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const ContextTypeSelectorSummary = styled(
  ContextTypeSelectionSmallText,
)<{ $centerAligned?: boolean }>`
  text-align: ${({ $centerAligned }) => ($centerAligned ? 'center' : 'left')};
`

export const ContextSelectorTableText = styled(ContextTypeSelectionBaseText)`
  text-align: left;
`

export const ContextSelectorTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 32px;
  flex-grow: 1;
`

export const ContextSelectorTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`

export const ContextSelectorOptionalDataContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  width: 175px;
`

export const ContextUpdateStatusContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
`

export const ContextTypeSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`

export const ContextTypeSummaryItem = styled.div<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 12px;
`

export const ContextTypeSummaryItemIcon = styled.div<{ $small?: boolean }>`
  flex-shrink: 0;
  svg {
    width: ${({ $small }) => ($small ? '14px' : '20px')};
    height: ${({ $small }) => ($small ? '14px' : '20px')};
  }
  svg {
    path {
      fill: ${palette.neutral[500]};
    }
  }
  ${outlineIconColor(palette.neutral[500])}
`

export const ContextTypeSummaryItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const ContextTypeSummaryItemTitle = ContextTypeSelectionBaseText

export const ContextTypeSummaryItemDescription = ContextTypeSelectionSmallText

export const ChatContextBooleanSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
`

export const ChatContextBooleanSelectorText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: ${palette.neutral[900]};
`

export const ChatContextBooleanCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
`

export const ChatContextBooleanSelectorPrompt = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: ${palette.neutral[900]};
`

export const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

export const PaginateButtonsContainer = styled.div`
  margin-left: auto;
`

export const ContextIdSelectorRow = styled(GridRow)`
  cursor: pointer;
`

export const ChipContainer = styled.div`
  margin-left: auto;
`

export const LinkItem = styled(Link)`
  svg {
    margin-left: 8px;
  }
`
